import React, { useEffect, useState } from "react";
import {
  CButton,
  CModal,
  CModalTitle,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CSpinner,
  CFormLabel,
  CFormInput,
  CFormSelect,
  CAlert,
  CTable,
  CCard,
  CCardHeader,
  CCardBody,
  CCollapse,
  CBadge,
} from "@coreui/react";
import { apiClient } from "services/Api";

// Transição única e irreversível Teórico -> Aprovado (PUT resource=approve).
// Fluxo: "form" (datas + cortes, com pré-visualização) -> "confirm" (resumo
// e confirmação explícita) -> "summary" (ETI resultante, se houve shift ou
// cortes).

const daysInMonth = (year, monthIndex) => new Date(year, monthIndex + 1, 0).getDate();

const isLastDayOfMonth = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return d === daysInMonth(y, m - 1);
};

// Espelha shift_calendar_date do backend: preserva o dia do mês, exceto
// quando a data original já é o último dia do seu mês, caso em que o
// resultado é também o último dia do mês de destino.
const shiftDateString = (dateStr, months) => {
  if (!dateStr || !months) return dateStr;
  const [y, m, d] = dateStr.split("-").map(Number);
  const totalMonths = y * 12 + (m - 1) + months;
  const newYear = Math.floor(totalMonths / 12);
  const newMonthIndex = ((totalMonths % 12) + 12) % 12;
  const lastDay = daysInMonth(newYear, newMonthIndex);
  const newDay = isLastDayOfMonth(dateStr) ? lastDay : Math.min(d, lastDay);
  return `${newYear}-${String(newMonthIndex + 1).padStart(2, "0")}-${String(
    newDay,
  ).padStart(2, "0")}`;
};

const monthsBetweenDates = (fromStr, toStr) => {
  const [y1, m1] = fromStr.split("-").map(Number);
  const [y2, m2] = toStr.split("-").map(Number);
  return (y2 - y1) * 12 + (m2 - m1);
};

// Escolhe o deslocamento "oficial" (usado por Contract/Sheet/Activity/
// Allocation): o campo que o utilizador editou por último ("condutor")
// manda no número de meses do projeto inteiro; o outro campo segue-o
// automaticamente, para que a duração do projeto (em meses) se mantenha
// sempre igual à original. Sem condutor definido (nenhum dos dois foi
// tocado ainda) usa-se o delta que estiver disponível.
const pickMonthShift = (startDelta, endDelta, drivenBy) => {
  if (drivenBy === "start" && startDelta !== null) return startDelta;
  if (drivenBy === "end" && endDelta !== null) return endDelta;
  return startDelta ?? endDelta ?? 0;
};

// Aplica `deltaMonths` ao mês/ano de `baseDateStr`, mas usa o dia de
// `dayFromDateStr` (limitado aos dias do mês de destino) — usado quando a
// data de fim escolhida implicaria um número de meses diferente do da data
// de início: mantém-se o dia que o utilizador escolheu para o fim, mas o
// mês segue sempre o deslocamento (único) usado por Contract/Activity.
const combineMonthFromDeltaWithDay = (baseDateStr, deltaMonths, dayFromDateStr) => {
  const [by, bm] = baseDateStr.split("-").map(Number);
  const day = Number(dayFromDateStr.split("-")[2]);
  const totalMonths = by * 12 + (bm - 1) + deltaMonths;
  const newYear = Math.floor(totalMonths / 12);
  const newMonthIndex = ((totalMonths % 12) + 12) % 12;
  const newDay = Math.min(day, daysInMonth(newYear, newMonthIndex));
  return `${newYear}-${String(newMonthIndex + 1).padStart(2, "0")}-${String(
    newDay,
  ).padStart(2, "0")}`;
};

// Calcula a data a mostrar/submeter para um campo (início ou fim): se é o
// campo condutor (o último editado), usa exatamente o que foi escrito; se
// não é condutor mas já tem um dia escolhido pelo utilizador numa edição
// anterior, mantém esse dia e segue o mês do deslocamento oficial; caso
// contrário (nunca tocado) usa o deslocamento por defeito, preservando o
// dia (ou "último dia do mês", ver `shiftDateString`).
const computePreviewDate = (currentDate, override, isDriver, deltaMonths) => {
  if (!currentDate) return "";
  if (isDriver && override) return override;
  if (override) return combineMonthFromDeltaWithDay(currentDate, deltaMonths, override);
  return shiftDateString(currentDate, deltaMonths);
};

const formatDatePT = (dateStr) => {
  if (!dateStr) return "—";
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
};

const formatMonthPT = (dateStr) => {
  if (!dateStr) return "—";
  const [y, m] = dateStr.split("-");
  return `${m}/${y}`;
};

// Fator aplicado às horas por um corte — o mesmo que `apply_rh_cut` aplica
// no backend a TODO o Work da pessoa nessa atividade. Aplicado a
// `total_hours` dá exatamente o total que o shift vai manter; nos meses do
// remanescente é uma aproximação (só os meses visíveis).
const cutFactor = (cut) => {
  if (!cut) return 1;
  if (cut.cutType === "total") return 0;
  if (cut.cutType === "percentage") {
    const pct = Number(cut.percentage) || 0;
    return Math.max(0, 1 - pct / 100);
  }
  return 1;
};

const cutKey = (personId, activityId) => `${personId}:${activityId}`;

const formatHours = (hours) => `${Number(hours || 0).toFixed(2)}h`;

// ETI é o total em ETI·mês das tabelas resumo (ETI mensal com 4 casas,
// por defeito, somado sobre os meses); a % é a taxa de dedicação média.
const formatEti = (eti) => Number(eti || 0).toFixed(4);

const formatPercentage = (value) => `${Number(value || 0).toFixed(2)}%`;

// Total atual -> total depois do corte (o que o shift mantém).
const renderHoursAfterCut = (totalHours, cut) => {
  const factor = cutFactor(cut);
  if (factor === 1) return formatHours(totalHours);
  return (
    <>
      <s className="text-muted">{formatHours(totalHours)}</s> →{" "}
      <strong>{formatHours(totalHours * factor)}</strong>
    </>
  );
};

// Tons claros do Bootstrap/CoreUI para destacar a linha: vermelho quando o
// RH é removido da atividade, amarelo quando é cortado.
const cutRowClassName = (cutType) => {
  if (cutType === "total") return "table-danger";
  if (cutType === "percentage") return "table-warning";
  return undefined;
};

const ApproveProject = ({ clientId, projectId, onClose, setToast }) => {
  const [project, setProject] = useState(null);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState("form");
  const [submitting, setSubmitting] = useState(false);
  const [shiftStartOverride, setShiftStartOverride] = useState("");
  const [shiftEndOverride, setShiftEndOverride] = useState("");
  // Qual dos dois campos foi editado por último — esse manda no
  // deslocamento em meses do projeto; o outro segue-o automaticamente.
  const [shiftDrivenBy, setShiftDrivenBy] = useState(null);
  const [rhCuts, setRhCuts] = useState({});
  const [expandedPeople, setExpandedPeople] = useState({});
  const [resultAffectations, setResultAffectations] = useState(null);
  const [removedFromTeam, setRemovedFromTeam] = useState([]);
  // Resultado da simulação (`dry_run`) que é mostrado no passo de
  // confirmação: ETI/horas finais, RH removidos da equipa, salários em falta.
  const [simulation, setSimulation] = useState(null);
  const [resultSalaries, setResultSalaries] = useState({ filled: [], missing: [] });

  const loading = !project || !preview;

  const currentStartDate = project?.management?.start_date || "";
  const currentEndDate = project?.management?.end_date || "";

  const startOverrideDelta =
    shiftStartOverride && currentStartDate
      ? monthsBetweenDates(currentStartDate, shiftStartOverride)
      : null;
  const endOverrideDelta =
    shiftEndOverride && currentEndDate
      ? monthsBetweenDates(currentEndDate, shiftEndOverride)
      : null;
  // Início e fim podem ser escolhidos em simultâneo, cada um com o seu dia
  // exato, mas Contract/Sheet/Activity só suportam um único deslocamento
  // em meses para todo o projeto: o campo editado por último decide esse
  // deslocamento (`shiftDrivenBy`) e o outro segue-o automaticamente, para
  // que a duração do projeto em meses se mantenha sempre a mesma.
  const monthShiftNumber = pickMonthShift(
    startOverrideDelta,
    endOverrideDelta,
    shiftDrivenBy,
  );

  const previewStartDate = computePreviewDate(
    currentStartDate,
    shiftStartOverride,
    shiftDrivenBy === "start",
    monthShiftNumber,
  );
  const previewEndDate = computePreviewDate(
    currentEndDate,
    shiftEndOverride,
    shiftDrivenBy === "end",
    monthShiftNumber,
  );
  // Comparação direta das datas (não só o delta em meses) — escolher um
  // dia diferente dentro do mesmo mês também conta como alteração.
  const hasDateShift =
    (!!previewStartDate && previewStartDate !== currentStartDate) ||
    (!!previewEndDate && previewEndDate !== currentEndDate);

  // Erros/faltas de capacidade da simulação anterior deixam de ser válidos
  // assim que o utilizador muda datas ou cortes.
  const clearSimulationErrors = () => setErrors({});

  const handleShiftByDate = (field, value) => {
    clearSimulationErrors();
    if (field === "start_date") {
      setShiftStartOverride(value);
      setShiftDrivenBy(value ? "start" : null);
    } else {
      setShiftEndOverride(value);
      setShiftDrivenBy(value ? "end" : null);
    }
  };

  const handleCutTypeChange = (personId, activityId, cutType) => {
    clearSimulationErrors();
    const key = cutKey(personId, activityId);
    setRhCuts((prev) => ({ ...prev, [key]: { ...prev[key], cutType } }));
  };

  const handleCutPercentageChange = (personId, activityId, value) => {
    clearSimulationErrors();
    const key = cutKey(personId, activityId);
    setRhCuts((prev) => ({ ...prev, [key]: { ...prev[key], percentage: value } }));
  };

  const togglePerson = (person) => {
    setExpandedPeople((prev) => ({ ...prev, [person]: prev[person] === false }));
  };

  const buildMonthShiftCuts = () =>
    Object.entries(rhCuts)
      .filter(([, cut]) => cut.cutType === "percentage" || cut.cutType === "total")
      .map(([key, cut]) => {
        const [personId, activityId] = key.split(":");
        return {
          person_id: Number(personId),
          activity_id: Number(activityId),
          cut_type: cut.cutType,
          percentage:
            cut.cutType === "percentage" ? Number(cut.percentage) : undefined,
        };
      });

  const buildPayload = (extra) => {
    const payload = {
      month_shift_cuts: buildMonthShiftCuts(),
      ...extra,
    };
    // Só envia datas quando há mesmo alteração — repor a data original
    // equivale a aprovar sem shift.
    if (hasDateShift) {
      // Envia sempre a data calculada na pré-visualização (já ajustada ao
      // deslocamento em meses do campo condutor) — o que se vê é o que é
      // submetido.
      payload.month_shift = monthShiftNumber;
      payload.month_shift_start_date = previewStartDate;
      payload.month_shift_end_date = previewEndDate;
    }
    return payload;
  };

  const handleRequestConfirmation = () => {
    const invalidCut = buildMonthShiftCuts().some(
      (cut) =>
        cut.cut_type === "percentage" &&
        (Number.isNaN(cut.percentage) || cut.percentage <= 0 || cut.percentage > 100),
    );
    if (invalidCut) {
      setErrors({
        month_shift_cuts: [
          "Indica uma percentagem maior que 0 e até 100 em todos os cortes percentuais.",
        ],
      });
      return;
    }
    if (previewStartDate && previewEndDate && previewStartDate >= previewEndDate) {
      setErrors({
        month_shift: ["A data de início deve ser anterior à data de conclusão."],
      });
      return;
    }
    setErrors({});
    // Simula a aprovação completa (o backend reverte tudo) para mostrar o
    // resultado previsto antes de confirmar — e detetar já aqui falta de
    // capacidade ou salários em falta.
    setSubmitting(true);
    apiClient
      .put(
        `/projects/management/${clientId}/${projectId}/approve/`,
        buildPayload({ dry_run: true }),
      )
      .then(({ data }) => {
        if (!data.can_confirm) {
          setErrors({
            month_shift: data.month_shift,
            month_shift_shortfalls: data.month_shift_shortfalls,
          });
          return;
        }
        setSimulation(data);
        setStep("confirm");
      })
      .catch((error) => {
        console.error("Erro ao simular a aprovação:", error);
        const resp = error.response;
        if (resp && resp.status === 400 && resp.data) {
          setErrors(resp.data);
        } else {
          setToast({
            title: "Aprovar Projeto",
            message: "Não foi possível simular a aprovação do projeto.",
            type: "error",
          });
        }
      })
      .finally(() => setSubmitting(false));
  };

  const handleConfirm = () => {
    setSubmitting(true);
    apiClient
      .put(
        `/projects/management/${clientId}/${projectId}/approve/`,
        buildPayload({ confirm_month_shift: true }),
      )
      .then((response) => {
        setResultAffectations(response.data?.month_shift_affectations || []);
        setRemovedFromTeam(response.data?.month_shift_removed_from_team || []);
        setResultSalaries({
          filled: response.data?.month_shift_filled_salaries || [],
          missing: response.data?.month_shift_missing_salaries || [],
        });
        setStep("summary");
      })
      .catch((error) => {
        console.error("Erro ao aprovar o projeto:", error);
        const resp = error.response;
        setStep("form");
        if (resp && resp.status === 400 && resp.data) {
          setErrors(resp.data);
          setToast({
            title: "Aprovar Projeto",
            message: "Não foi possível aprovar o projeto. Verifica os erros assinalados.",
            type: "warning",
          });
        } else {
          setToast({
            title: "Aprovar Projeto",
            message: "Não foi possível aprovar o projeto.",
            type: "error",
          });
        }
      })
      .finally(() => setSubmitting(false));
  };

  useEffect(() => {
    Promise.all([
      apiClient.get(`/projects/management/${clientId}/${projectId}/project/`),
      apiClient.put(`/projects/management/${clientId}/${projectId}/approve/`, {}),
    ])
      .then(([projectResponse, previewResponse]) => {
        setProject(projectResponse.data);
        setPreview({
          affectations: previewResponse.data.month_shift_current_affectations || [],
          rhSheets: previewResponse.data.month_shift_rh_sheets || [],
        });
      })
      .catch((error) => {
        console.error("Erro ao carregar a pré-visualização da aprovação:", error);
        const message = error.response?.data?.non_field_errors;
        setToast({
          title: "Aprovar Projeto",
          message: Array.isArray(message)
            ? message.join(" ")
            : "Não foi possível carregar os dados do projeto.",
          type: "error",
        });
        onClose();
      });
  }, [clientId, projectId, onClose, setToast]);

  const renderErrors = () => {
    const messages = ["non_field_errors", "month_shift", "month_shift_cuts", "state"]
      .flatMap((field) => {
        const message = errors?.[field];
        if (!message) return [];
        return Array.isArray(message) ? message : [message];
      })
      .filter((m) => typeof m === "string");
    if (messages.length === 0) return null;
    return (
      <CAlert color="danger" className="py-2">
        {messages.length === 1 ? (
          messages[0]
        ) : (
          <ul className="mb-0 ps-3">
            {messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        )}
      </CAlert>
    );
  };

  // RH/atividades cujas horas não couberam nos novos meses sem sobrelotar
  // a pessoa (resposta 400 da confirmação) — assinalados no respetivo card.
  const shortfallByKey = new Map(
    (errors?.month_shift_shortfalls || []).map((item) => [
      cutKey(item.person_id, item.activity_id),
      item,
    ]),
  );

  // Agrupa a afetação por pessoa — cada RH com horas fica sempre com um
  // card próprio (com as atividades e os controlos de corte), mesmo sem
  // ter conflitos de sheet partilhada com outro projeto.
  const groupAffectationsByPerson = (affectations) => {
    const map = new Map();
    affectations.forEach((a) => {
      if (!map.has(a.person_id)) {
        map.set(a.person_id, { personId: a.person_id, person: a.person, rows: [] });
      }
      map.get(a.person_id).rows.push(a);
    });
    return Array.from(map.values()).sort((a, b) =>
      a.person.localeCompare(b.person, "pt"),
    );
  };

  // Meses em conflito (remanescente ainda negativo depois de aplicar os
  // cortes pendentes) desta pessoa — fonte única para o badge "X meses em
  // conflito" e para as mensagens de conflito no card, para os dois
  // ficarem sempre coerentes entre si e reagirem em tempo real ao corte.
  const getConflictMonths = (personId, sheet) => {
    if (!sheet || sheet.months.length === 0) return [];

    const deltaByMonth = sheet.months.map((_month, mIdx) =>
      sheet.activities.reduce((sum, act) => {
        if (!act.is_current_project) return sum;
        const factor = cutFactor(rhCuts[cutKey(personId, act.activity_id)]);
        return sum + act.hours[mIdx] * (1 - factor);
      }, 0),
    );

    return sheet.months
      .map((month, mIdx) => {
        const remaining = sheet.remaining_hours[mIdx];
        if (remaining == null) return null;
        const adjusted = remaining + deltaByMonth[mIdx];
        return adjusted < 0 ? { month, adjusted } : null;
      })
      .filter(Boolean);
  };

  const renderRhCard = ({ personId, person, rows }, sheet) => {
    // Expandido por omissão — os controlos de corte são o elemento
    // principal do card, não algo secundário a abrir por conflito.
    const expanded = expandedPeople[person] !== false;
    const conflictMonths = getConflictMonths(personId, sheet);
    const hasShortfall = rows.some((a) =>
      shortfallByKey.has(cutKey(a.person_id, a.activity_id)),
    );
    return (
      <CCard key={personId} className="mb-2">
        <CCardHeader
          className="d-flex justify-content-between align-items-center"
          style={{ cursor: "pointer" }}
          onClick={() => togglePerson(person)}
        >
          <span>
            {person}{" "}
            {conflictMonths.length > 0 && (
              <CBadge color="warning" className="ms-2">
                {conflictMonths.length}{" "}
                {conflictMonths.length === 1 ? "mês em conflito" : "meses em conflito"}
              </CBadge>
            )}
            {hasShortfall && (
              <CBadge color="danger" className="ms-2">
                Sem capacidade nos novos meses
              </CBadge>
            )}
          </span>
          <span>{expanded ? "▲" : "▼"}</span>
        </CCardHeader>
        <CCollapse visible={expanded}>
          <CCardBody>
            <CTable small bordered className="mb-0">
              <thead>
                <tr>
                  <th>Atividade</th>
                  <th title="Total em ETI·mês, igual ao das tabelas resumo">
                    ETI·mês atual
                  </th>
                  <th title="Taxa de dedicação média na janela da afetação">
                    Dedicação atual
                  </th>
                  <th>Horas</th>
                  <th style={{ width: "30%" }}>Corte</th>
                  <th style={{ width: "15%" }}>%</th>
                </tr>
              </thead>
              <tbody>
                {[...rows]
                  .sort((a, b) => a.activity.localeCompare(b.activity, "pt"))
                  .map((a) => {
                    const cut = rhCuts[cutKey(a.person_id, a.activity_id)] || {
                      cutType: "none",
                      percentage: "",
                    };
                    const shortfall = shortfallByKey.get(
                      cutKey(a.person_id, a.activity_id),
                    );
                    return (
                      <tr key={a.activity_id} className={cutRowClassName(cut.cutType)}>
                        <td>
                          {a.activity}
                          {shortfall && (
                            <div className="text-danger small">
                              {shortfall.reason === "no_months"
                                ? `Sem meses disponíveis para ${shortfall.missing_hours}h`
                                : `Faltam ${shortfall.missing_hours}h de capacidade`}
                            </div>
                          )}
                        </td>
                        <td>{formatEti(a.eti_value)}</td>
                        <td>{formatPercentage(a.percentage_value)}</td>
                        <td className="text-nowrap">
                          {renderHoursAfterCut(a.total_hours, cut)}
                        </td>
                        <td>
                          <CFormSelect
                            size="sm"
                            value={cut.cutType}
                            options={[
                              { value: "none", label: "Sem alteração" },
                              { value: "percentage", label: "Corte percentual" },
                              { value: "total", label: "Remover completamente" },
                            ]}
                            onChange={(e) =>
                              handleCutTypeChange(a.person_id, a.activity_id, e.target.value)
                            }
                          />
                        </td>
                        <td>
                          {cut.cutType === "percentage" && (
                            <CFormInput
                              size="sm"
                              type="number"
                              min={0}
                              max={100}
                              value={cut.percentage}
                              onChange={(e) =>
                                handleCutPercentageChange(
                                  a.person_id,
                                  a.activity_id,
                                  e.target.value,
                                )
                              }
                              placeholder="ex.: 30"
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </CTable>

            {conflictMonths.length > 0 && (
              <div className="mt-2">
                {conflictMonths.map(({ month, adjusted }) => (
                  <div key={month} className="text-danger small">
                    Conflito: {formatMonthPT(month)} com o remanescente{" "}
                    {adjusted.toFixed(1)}h
                  </div>
                ))}
              </div>
            )}
          </CCardBody>
        </CCollapse>
      </CCard>
    );
  };

  const renderShiftBadge = () => {
    if (monthShiftNumber !== 0) {
      return (
        <CBadge color="info">
          {monthShiftNumber > 0
            ? `Adia ${monthShiftNumber}`
            : `Antecipa ${Math.abs(monthShiftNumber)}`}{" "}
          {Math.abs(monthShiftNumber) === 1 ? "mês" : "meses"}
        </CBadge>
      );
    }
    return hasDateShift ? <CBadge color="info">Ajusta apenas o dia</CBadge> : null;
  };

  const renderDateRow = (label, current, next) => (
    <div className="d-flex justify-content-between">
      <span className="text-muted">{label}</span>
      <span>
        {formatDatePT(current)}
        {next !== current && (
          <>
            <span className="mx-2">➜</span>
            <strong className="text-primary">{formatDatePT(next)}</strong>
          </>
        )}
      </span>
    </div>
  );

  // Tabela ETI/horas antes -> depois por RH/atividade. "Antes" vem da
  // pré-visualização inicial; "depois" da simulação ou do resultado final.
  // Pares que desaparecem (corte total) aparecem como removidos.
  const renderEtiComparison = (afterAffectations) => {
    const before = new Map(
      preview.affectations.map((a) => [cutKey(a.person_id, a.activity_id), a]),
    );
    const after = new Map(
      afterAffectations.map((a) => [cutKey(a.person_id, a.activity_id), a]),
    );
    const keys = Array.from(new Set([...before.keys(), ...after.keys()]));
    const rows = keys
      .map((key) => ({ key, before: before.get(key), after: after.get(key) }))
      .sort((x, y) => {
        const a = x.after || x.before;
        const b = y.after || y.before;
        return (
          a.person.localeCompare(b.person, "pt") ||
          a.activity.localeCompare(b.activity, "pt")
        );
      });
    if (rows.length === 0) return <p className="text-muted mb-0">Sem RH.</p>;

    return (
      <CTable small bordered className="mb-0">
        <thead>
          <tr>
            <th>RH</th>
            <th>Atividade</th>
            <th title="Total em ETI·mês, igual ao das tabelas resumo">
              ETI·mês antes → depois
            </th>
            <th title="Taxa de dedicação média na janela da afetação">
              Dedicação antes → depois
            </th>
            <th>Horas antes → depois</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ key, before: b, after: a }) => {
            const ref = a || b;
            if (!a) {
              return (
                <tr key={key} className="table-danger">
                  <td>{ref.person}</td>
                  <td>{ref.activity}</td>
                  <td>{formatEti(b.eti_value)} → —</td>
                  <td>{formatPercentage(b.percentage_value)} → —</td>
                  <td>{formatHours(b.total_hours)} → removido</td>
                </tr>
              );
            }
            // ETI é reportado com 4 casas: qualquer diferença nessa precisão
            // fica destacada.
            const etiChanged =
              b && Math.abs(Number(a.eti_value) - Number(b.eti_value)) >= 0.0001;
            return (
              <tr key={key}>
                <td>{ref.person}</td>
                <td>{ref.activity}</td>
                <td className={etiChanged ? "table-warning" : undefined}>
                  {b ? formatEti(b.eti_value) : "—"} →{" "}
                  <strong>{formatEti(a.eti_value)}</strong>
                </td>
                <td>
                  {b ? formatPercentage(b.percentage_value) : "—"} →{" "}
                  <strong>{formatPercentage(a.percentage_value)}</strong>
                </td>
                <td className="text-nowrap">
                  {b ? formatHours(b.total_hours) : "—"} →{" "}
                  <strong>{formatHours(a.total_hours)}</strong>
                </td>
              </tr>
            );
          })}
        </tbody>
      </CTable>
    );
  };

  const renderRemovedFromTeam = (removed) =>
    removed.length > 0 && (
      <CAlert color="danger" className="py-2 mt-3 mb-0">
        {removed.map((r) => r.person).join(", ")}{" "}
        {removed.length === 1 ? "é removido" : "são removidos"} da equipa do
        projeto (sem horas em nenhuma atividade). As Sheets da pessoa
        (jornada/salário/SS) são mantidas.
      </CAlert>
    );

  const renderSalaryList = (items) => (
    <ul className="mb-0 ps-3">
      {items.map((item) => (
        <li key={item.person_id}>
          {item.person}: {item.months_label.join(", ")}
        </li>
      ))}
    </ul>
  );

  // Salário/SS são deslocados com as datas; meses em falta assumem o valor
  // do mês conhecido mais próximo da pessoa (informativo, não bloqueia).
  const renderSalaryNotes = (filled, missing) => (
    <>
      {filled.length > 0 && (
        <CAlert color="info" className="py-2 mt-3 mb-0">
          <div className="mb-1">
            Salário/SS em falta assumidos a partir do mês conhecido mais próximo:
          </div>
          {renderSalaryList(filled)}
        </CAlert>
      )}
      {missing.length > 0 && (
        <CAlert color="warning" className="py-2 mt-3 mb-0">
          <div className="mb-1">
            Salário/SS que ficam por preencher (sem valor de referência da
            pessoa, ou mês usado por outro projeto):
          </div>
          {renderSalaryList(missing)}
        </CAlert>
      )}
    </>
  );

  const renderForm = () => {
    const affectationGroups = groupAffectationsByPerson(preview.affectations);
    const sheetsByPersonId = new Map(
      preview.rhSheets.map((sheet) => [sheet.person_id, sheet]),
    );

    return (
      <>
        {renderErrors()}
        <CAlert color="warning" className="py-2">
          A aprovação só pode ser feita uma vez e não pode ser revertida: depois
          de aprovado, o projeto não volta ao estado Teórico.
        </CAlert>

        <h6 className="mt-3">1. Datas do projeto (opcional)</h6>
        <div className="p-3 border rounded bg-light mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong>Pré-visualização</strong>
            {renderShiftBadge()}
          </div>
          {renderDateRow("Início do projeto:", currentStartDate, previewStartDate)}
          {renderDateRow("Fim do projeto:", currentEndDate, previewEndDate)}
          {!hasDateShift && (
            <small className="text-muted d-block mt-2">
              Sem alterações. Escolhe abaixo a nova data de início e/ou de fim do
              projeto para veres aqui o impacto.
            </small>
          )}
        </div>

        <div className="d-flex">
          <div className="col-6 pe-3">
            <CFormLabel htmlFor="newStartDate" className="small text-muted">
              Nova data de início
            </CFormLabel>
            <CFormInput
              id="newStartDate"
              type="date"
              value={previewStartDate}
              disabled={!currentStartDate}
              onChange={(e) => handleShiftByDate("start_date", e.target.value)}
            />
          </div>
          <div className="col-6">
            <CFormLabel htmlFor="newEndDate" className="small text-muted">
              Nova data de fim
            </CFormLabel>
            <CFormInput
              id="newEndDate"
              type="date"
              value={previewEndDate}
              disabled={!currentEndDate}
              onChange={(e) => handleShiftByDate("end_date", e.target.value)}
            />
          </div>
        </div>
        <small className="text-muted d-block mt-1">
          O projeto mantém sempre a mesma duração em meses entre início e fim: ao
          alterar uma das datas, a outra é recalculada automaticamente para
          preservar essa duração (o dia de cada uma pode ser ajustado livremente).
          As atividades, contratos, alocações e imputações são deslocados pelo
          número de meses inteiro correspondente. Os dias úteis são recalculados
          para as datas exatas e o total de horas de cada RH por atividade é
          mantido — ou ajustado ao corte indicado, quando existe — e
          redistribuído pelos novos meses sem ultrapassar a capacidade mensal da
          pessoa (somando os outros projetos). Se não couber, a aprovação é
          recusada e podes aplicar um corte.
        </small>

        <h6 className="mt-4">2. Afetação atual e cortes por RH (opcional)</h6>
        <small className="text-muted d-block mb-2">
          Podes cortar (parcial ou totalmente) a afetação de um RH numa atividade
          — as horas são recalculadas de acordo com o corte escolhido.
        </small>
        {affectationGroups.length === 0 ? (
          <small className="text-muted">Sem RH com horas neste projeto.</small>
        ) : (
          affectationGroups.map((group) =>
            renderRhCard(group, sheetsByPersonId.get(group.personId)),
          )
        )}

      </>
    );
  };

  const renderConfirmation = () => {
    const cuts = buildMonthShiftCuts();
    const affectationByKey = new Map(
      preview.affectations.map((a) => [cutKey(a.person_id, a.activity_id), a]),
    );
    return (
      <>
        <CAlert color="danger" className="py-2">
          <strong>Confirma a aprovação do projeto {project.name}.</strong> Esta
          operação é irreversível: o projeto passa a Aprovado, não poderá voltar
          ao estado Teórico e esta aprovação não pode ser repetida.
        </CAlert>

        <h6>Datas do projeto</h6>
        {hasDateShift ? (
          <div className="p-3 border rounded bg-light mb-3">
            <div className="mb-2">{renderShiftBadge()}</div>
            {renderDateRow("Início do projeto:", currentStartDate, previewStartDate)}
            {renderDateRow("Fim do projeto:", currentEndDate, previewEndDate)}
          </div>
        ) : (
          <p className="text-muted">Sem alteração de datas.</p>
        )}

        <h6>Cortes de afetação</h6>
        {cuts.length === 0 ? (
          <p className="text-muted mb-0">Sem cortes.</p>
        ) : (
          <CTable small bordered className="mb-0">
            <thead>
              <tr>
                <th>RH</th>
                <th>Atividade</th>
                <th>Corte</th>
                <th>Horas</th>
              </tr>
            </thead>
            <tbody>
              {cuts.map((cut) => {
                const a = affectationByKey.get(cutKey(cut.person_id, cut.activity_id));
                return (
                  <tr
                    key={cutKey(cut.person_id, cut.activity_id)}
                    className={cutRowClassName(cut.cut_type)}
                  >
                    <td>{a?.person}</td>
                    <td>{a?.activity}</td>
                    <td>
                      {cut.cut_type === "total"
                        ? "Remover completamente"
                        : `Corte de ${cut.percentage}%`}
                    </td>
                    <td className="text-nowrap">
                      {renderHoursAfterCut(a?.total_hours, {
                        cutType: cut.cut_type,
                        percentage: cut.percentage,
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </CTable>
        )}

        <h6 className="mt-4">Resultado previsto</h6>
        {renderEtiComparison(simulation?.month_shift_affectations || [])}
        <small className="text-muted d-block mt-1">
          O total de horas é mantido (ou ajustado ao corte). O ETI·mês (o
          mesmo das tabelas resumo) pode variar quando os novos meses têm um
          número diferente de dias úteis — a amarelo quando muda (4 casas
          decimais).
        </small>
        {renderRemovedFromTeam(simulation?.month_shift_removed_from_team || [])}
        {renderSalaryNotes(
          simulation?.month_shift_filled_salaries || [],
          simulation?.month_shift_missing_salaries || [],
        )}
      </>
    );
  };

  const renderSummary = () => (
    <>
      <CAlert color="success" className="py-2">
        Projeto aprovado com sucesso — segue a afetação (ETI) atual de cada RH
        por atividade.
      </CAlert>
      {renderEtiComparison(resultAffectations)}
      {renderRemovedFromTeam(removedFromTeam)}
      {renderSalaryNotes(resultSalaries.filled, resultSalaries.missing)}
    </>
  );

  const renderBody = () => {
    if (step === "summary") return renderSummary();
    if (loading) {
      return (
        <div className="d-flex justify-content-center py-4">
          <CSpinner />
        </div>
      );
    }
    if (step === "confirm") return renderConfirmation();
    return renderForm();
  };

  const renderFooter = () => {
    if (step === "summary") {
      return (
        <CButton color="primary" onClick={() => window.location.reload()}>
          Fechar
        </CButton>
      );
    }
    if (step === "confirm") {
      return (
        <>
          <CButton
            color="secondary"
            variant="outline"
            disabled={submitting}
            onClick={() => setStep("form")}
          >
            Voltar
          </CButton>
          <CButton color="success" disabled={submitting} onClick={handleConfirm}>
            {submitting && <CSpinner size="sm" className="me-2" />}
            Confirmar aprovação
          </CButton>
        </>
      );
    }
    return (
      <>
        <CButton color="secondary" onClick={onClose}>
          Cancelar
        </CButton>
        <CButton
          color="success"
          disabled={loading || submitting}
          onClick={handleRequestConfirmation}
        >
          {submitting && <CSpinner size="sm" className="me-2" />}
          Aprovar Projeto
        </CButton>
      </>
    );
  };

  return (
    <CModal
      onClose={step === "summary" ? () => window.location.reload() : onClose}
      size="xl"
      backdrop="static"
      scrollable
      visible
    >
      <CModalHeader closeButton={!submitting}>
        <CModalTitle>
          {step === "confirm" ? "Confirmar aprovação" : "Aprovar Projeto"}{" "}
          {project ? project.name : <CSpinner size="sm" />}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>{renderBody()}</CModalBody>
      <CModalFooter>{renderFooter()}</CModalFooter>
    </CModal>
  );
};

export default ApproveProject;
